import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { ChecklistStatus, RecordLike } from '../../shared/checklistUtils';
import { getStatusLabel, isFiniteNumber, readNumber, readString } from '../../shared/checklistUtils';

import type { DatesRestrictionsChecklistRow } from './types';

const DATES_RESTRICTIONS_FIELDS = {
  flexiDealOptions: 'Flexi night + deal options',
  flexiNightMargin: 'Flexi night margin',
  offerDiscounts: 'Offer discounts',
} as const;

const DEAL_OFFER_DISCOUNT_FIELD = 'offer_discount__c';
const EXTRA_NIGHT_MARGIN_FIELDS = ['extra_night_margin', 'extra_night_margin__c', 'Extra_Night_Margin__c'];

export function buildDatesRestrictionsRows({
  campaignType,
  opportunityDeals,
  packages,
}: {
  campaignType?: string;
  opportunityDeals?: Array<OpportunityRelatedDeal>;
  packages?: Array<App.Package>;
}): Array<DatesRestrictionsChecklistRow> {
  const packageRows = packages ?? [];
  const opportunityDealsByPackageId = mapOpportunityDealsByPackageId(opportunityDeals);
  const flexiPackages = packageRows.filter((offerPackage) => !!(offerPackage as unknown as RecordLike).flexible_nights);

  return [
    buildFlexiDealOptionsRow({ campaignType, flexiPackages }),
    buildOfferDiscountsRow({ opportunityDealsByPackageId, packages: packageRows }),
    buildFlexiNightMarginRow({ flexiPackages, opportunityDealsByPackageId }),
  ];
}

function buildFlexiDealOptionsRow({
  campaignType,
  flexiPackages,
}: {
  campaignType?: string;
  flexiPackages: Array<App.Package>;
}): DatesRestrictionsChecklistRow {
  const normalizedCampaignType = campaignType?.trim();
  const isFlexiCampaign = normalizedCampaignType ? /flexi/i.test(normalizedCampaignType) : undefined;
  const flexiPackageCount = flexiPackages.length;
  const hasExpectedFlexiPackages = hasExpectedFlexiPackageCount(isFlexiCampaign, flexiPackageCount);
  const status: ChecklistStatus = hasExpectedFlexiPackages ? 'approved' : 'requires approval';

  return {
    details: [
      { label: 'Campaign type', value: normalizedCampaignType || 'Not available' },
      { label: 'Flexi deal options', value: String(flexiPackageCount) },
      {
        label: 'Flexi option names',
        value: flexiPackages.map(getPackageDisplayName).join(', ') || 'None',
      },
    ],
    field: DATES_RESTRICTIONS_FIELDS.flexiDealOptions,
    id: 'dates-restrictions:flexi-deal-options',
    rule: 'If flexi night, need at least 1 deal option with flexi night. If standard, no deal options with flexi night.',
    sourceValue:
      typeof isFlexiCampaign === 'boolean'
        ? `${normalizedCampaignType}; ${flexiPackageCount} flexi deal option${flexiPackageCount === 1 ? '' : 's'}`
        : `Campaign type unavailable; ${flexiPackageCount} flexi deal option${flexiPackageCount === 1 ? '' : 's'}`,
    status,
    statusLabel: getStatusLabel(status),
    summary: hasExpectedFlexiPackages
      ? 'Flexi-night setup aligns with loaded deal options.'
      : 'Flexi-night setup needs review.',
    title: DATES_RESTRICTIONS_FIELDS.flexiDealOptions,
  };
}

function hasExpectedFlexiPackageCount(isFlexiCampaign: boolean | undefined, flexiPackageCount: number) {
  if (isFlexiCampaign === undefined) {
    return false;
  }

  if (isFlexiCampaign) {
    return flexiPackageCount >= 1;
  }

  return flexiPackageCount === 0;
}

function buildOfferDiscountsRow({
  opportunityDealsByPackageId,
  packages,
}: {
  opportunityDealsByPackageId: Map<string, OpportunityRelatedDeal>;
  packages: Array<App.Package>;
}): DatesRestrictionsChecklistRow {
  const discountDetails = packages.map((offerPackage) => {
    const packageRecord = offerPackage as unknown as RecordLike;
    const packageId = readString(packageRecord, ['id_salesforce_external']);
    const opportunityDeal = packageId ? opportunityDealsByPackageId.get(packageId) : undefined;
    const dealRecord = (opportunityDeal ?? {}) as unknown as RecordLike;
    const discount = readNumber(dealRecord, [DEAL_OFFER_DISCOUNT_FIELD]);

    return {
      discount,
      name: getPackageDisplayName(offerPackage),
    };
  });
  const hasPackages = discountDetails.length > 0;
  const hasAllDiscounts = discountDetails.every(({ discount }) => isFiniteNumber(discount));
  const hasOutOfRangeDiscount = discountDetails.some(
    ({ discount }) => isFiniteNumber(discount) && (discount < 15 || discount > 75),
  );
  const status: ChecklistStatus =
    hasPackages && hasAllDiscounts && !hasOutOfRangeDiscount ? 'approved' : 'requires approval';

  return {
    details: discountDetails.map(({ discount, name }) => ({
      label: name,
      value: isFiniteNumber(discount) ? `${discount}%` : 'Not available',
    })),
    field: DATES_RESTRICTIONS_FIELDS.offerDiscounts,
    id: 'dates-restrictions:offer-discounts',
    rule: 'If any offer discount is greater than 75% or less than 15%, flag for approval; otherwise auto-approve.',
    sourceValue: hasPackages
      ? discountDetails
          .map(({ discount, name }) => `${name}: ${isFiniteNumber(discount) ? `${discount}%` : 'N/A'}`)
          .join('; ')
      : 'No deal options available',
    status,
    statusLabel: getStatusLabel(status),
    summary:
      hasPackages && hasAllDiscounts && !hasOutOfRangeDiscount
        ? 'Offer discounts are within the approved range.'
        : 'Offer discounts need review.',
    title: DATES_RESTRICTIONS_FIELDS.offerDiscounts,
  };
}

function buildFlexiNightMarginRow({
  flexiPackages,
  opportunityDealsByPackageId,
}: {
  flexiPackages: Array<App.Package>;
  opportunityDealsByPackageId: Map<string, OpportunityRelatedDeal>;
}): DatesRestrictionsChecklistRow {
  const marginDetails = flexiPackages.map((offerPackage) => {
    const packageRecord = offerPackage as unknown as RecordLike;
    const packageId = readString(packageRecord, ['id_salesforce_external']);
    const opportunityDeal = packageId ? opportunityDealsByPackageId.get(packageId) : undefined;
    const margin = isFiniteNumber(opportunityDeal?.extra_night_margin__c)
      ? opportunityDeal.extra_night_margin__c
      : readNumber(packageRecord, EXTRA_NIGHT_MARGIN_FIELDS);

    return {
      margin,
      name: getPackageDisplayName(offerPackage),
    };
  });
  const hasFlexiPackages = marginDetails.length > 0;
  const hasAllMargins = marginDetails.every(({ margin }) => isFiniteNumber(margin));
  const hasOutOfRangeMargin = marginDetails.some(
    ({ margin }) => isFiniteNumber(margin) && (margin < 10 || margin > 50),
  );
  const status: ChecklistStatus =
    !hasFlexiPackages || (hasAllMargins && !hasOutOfRangeMargin) ? 'approved' : 'requires approval';

  return {
    details: hasFlexiPackages
      ? marginDetails.map(({ margin, name }) => ({
          label: name,
          value: isFiniteNumber(margin) ? `${margin}%` : 'Not available',
        }))
      : [{ label: 'Flexi deal options', value: 'None' }],
    field: DATES_RESTRICTIONS_FIELDS.flexiNightMargin,
    id: 'dates-restrictions:flexi-night-margin',
    rule: 'If any flexi night margin is below 10% or above 50%, flag for approval; otherwise auto-approve.',
    sourceValue: hasFlexiPackages
      ? marginDetails.map(({ margin, name }) => `${name}: ${isFiniteNumber(margin) ? `${margin}%` : 'N/A'}`).join('; ')
      : 'No flexi-night deal options',
    status,
    statusLabel: getStatusLabel(status),
    summary:
      !hasFlexiPackages || (hasAllMargins && !hasOutOfRangeMargin)
        ? 'Flexi night margins are within approved range.'
        : 'Flexi night margins need review.',
    title: DATES_RESTRICTIONS_FIELDS.flexiNightMargin,
  };
}

function getPackageDisplayName(offerPackage: App.Package) {
  return (
    offerPackage.deal_option_name || offerPackage.name || String(offerPackage.id_salesforce_external || 'Deal option')
  );
}

function mapOpportunityDealsByPackageId(opportunityDeals?: Array<OpportunityRelatedDeal>) {
  return new Map(
    (opportunityDeals ?? [])
      .map((deal) => {
        const dealRecord = deal as unknown as RecordLike;
        const dealId = readString(dealRecord, ['id', 'sfid']);

        return dealId ? ([dealId, deal] as const) : undefined;
      })
      .filter((dealEntry): dealEntry is readonly [string, OpportunityRelatedDeal] => !!dealEntry),
  );
}
